import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";

// Origin-aware back link / breadcrumb for detail screens — one consistent look & behavior.
// If we arrived carrying `state.from` (see lib/nav.fromState), we return EXACTLY there
// (path + query) with a truthful label. Otherwise we fall back to the section default
// (`to` / `label`) so deep-links and refreshes still go somewhere sensible.
// Pass `current` to render a breadcrumb crumb after the back link.
export function BackLink({ to, label, current, onClick, testid = "back-link", className = "" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromApp = !!location.state?.from;
  // arrived from another screen of the app without a trail (a list, a card, a report) — Back goes back THERE
  const hasAppHistory = (window.history.state?.idx || 0) > 0;
  const text = location.state?.fromLabel || (hasAppHistory && !cameFromApp ? "Back" : label);
  // If we arrived via an in-app link, pop real browser history — that restores the exact
  // prior screen AND its own origin state, so a chain (search → part → donor → back → back)
  // walks back correctly. On deep-links / refresh (no state) fall back to the section default.
  const handle = onClick || (() => (cameFromApp || hasAppHistory ? navigate(-1) : (to ? navigate(to) : navigate(-1))));
  return (
    <nav data-testid={`${testid}-trail`} className={`flex items-center gap-1.5 text-sm min-w-0 ${className}`}>
      <button
        data-testid={testid}
        onClick={handle}
        className="inline-flex items-center gap-1.5 font-medium text-slate-500 hover:text-slate-800 transition-colors shrink-0"
      >
        <ArrowLeft className="w-4 h-4" /> {text}
      </button>
      {current && (
        <>
          <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
          <span data-testid={`${testid}-current`} className="text-slate-700 font-medium truncate">{current}</span>
        </>
      )}
    </nav>
  );
}
